import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Box, Grid, InputLabel, makeStyles } from '@material-ui/core';
import { Form as FormEngine, FormDefinition } from '@mylicenseone/forms';
import { Person } from '../../../../../store/modules/person/types';
import { DropDownField, Form, LicenseTypeConfiguration } from '../../../../../store/modules/application/types';
import { Api } from '../../../../../store/services/services';
import { useAuthenticationContext } from '../../../../../authentication';
import FormFieldDisplay from '../../../../../components/FormDisplay/FormFieldDisplay';
import { restoreRecord } from '../../../../../components/FormDisplay/FormDisplay';
import { ErrorState } from '../../../../../components/FormDisplay/types';
import AutoCompleteSelect from '../../../../../components/AutoCompleteSelect';

type AddLicenseDialogProps = {
    setIsAddLicenseModalOpen: Function;
    licenseTypesList: LicenseTypeConfiguration[];
    addLicense: Function;
    person: Person;
};

const AddLicenseDialog: FunctionComponent<AddLicenseDialogProps> = (props) => {
    const history = useHistory();
    const authContext = useAuthenticationContext();
    const { setIsAddLicenseModalOpen, addLicense, licenseTypesList, person } = props;
    const [licenseTypeId, setLicenseTypeId] = useState<string>('');
    const [addForm, setAddForm] = useState<Form>();
    const [addFormLoading, setAddFormLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<Record<string, unknown>>({});
    const [formEngine, setFormEngine] = useState<FormEngine>();
    const [formErrors, setFormErrors] = useState<Record<string, ErrorState>>({});
    const [licenseValue, setLicenseValue] = useState<DropDownField | null>(null);
    const formErrorsRef = useRef<Record<string, ErrorState>>({});

    useEffect(() => {
        formEngine?.evaluateApplicationData(formData, { validateDefinedOnly: true });
    }, [formEngine, formData]);

    useEffect(() => {
        if (addForm?.sections) {
            const engine = new FormEngine(addForm as FormDefinition);

            engine.onValidationChange((field, error, messages) => {
                formErrorsRef.current = {
                    ...formErrorsRef.current,
                    [field]: {
                        error,
                        messages,
                    },
                };

                setFormErrors(formErrorsRef.current);
            });

            setFormEngine(engine);
        } else {
            setFormEngine(undefined);
        }

        setFormData({});
        setFormErrors({});
    }, [addForm]);

    const handleChangeLicenseType = async (data: DropDownField | null) => {
        const id = data?.value;
        const licenseType = licenseTypesList.find((licenseType: LicenseTypeConfiguration) => licenseType.id === id);
        setLicenseValue(data);
        if (licenseType) {
            setAddFormLoading(true);
            id && setLicenseTypeId(id);
            setFormData({});
            setFormErrors({});

            if (licenseType.views?.add) {
                const token = await authContext.getAccessToken({ scopes: [authContext.scopes['forms.read']] });
                const form: Form = await Api.getForm(token.accessToken, licenseType.views.add.formId);

                setAddForm(form);
            } else {
                setAddForm(undefined);
            }

            setAddFormLoading(false);
        }
    };

    const handleInputChange = (dataKey: any, value: unknown) => {
        setFormData({
            ...formData,
            [dataKey]: value,
        });
    };

    const handleAddLicenseChange = async () => {
        await formEngine?.evaluateApplicationData(formData);

        if (!formEngine?.applicationHasErrors()) {
            const licenseType = licenseTypesList.find((licType) => licType.id === licenseTypeId);

            if (licenseType) {
                const license = addForm ? restoreRecord({}, formData, addForm) : undefined;

                const addLicensePayload = {
                    applications: [],
                    ...license,
                    type: {
                        id: licenseType.id,
                        name: licenseType.name,
                    },
                    person: {
                        id: person.id,
                        name: person.name,
                        address: person.address,
                    },
                };

                addLicense(addLicensePayload, history);
                setIsAddLicenseModalOpen(false);
            }
        }
    };

    const classes = makeStyles(() => ({
        detailNodeLabel: {
            fontSize: 14,
            fontWeight: 500,
            color: '#424242',
        },
    }))();

    return (
        <Box className="add-dialog">
            <Grid container className="activation-modal">
                <div className="modal-header">Add License</div>
                <Box component="div" className="add-dialog-main">
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid
                            item
                            xs={3}
                            sm={3}
                            md={3}
                            lg={3}
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                        >
                            <Box component="div" className={classes.detailNodeLabel}>
                                License Type
                            </Box>
                        </Grid>
                        <Grid item xs={9} sm={9} md={9} lg={9}>
                            <Box>
                                <InputLabel htmlFor="type-select-native" style={{ display: 'none' }}>
                                    License Type
                                </InputLabel>
                                <AutoCompleteSelect
                                    id="type-select"
                                    data={licenseTypesList.map((type) => ({
                                        label: type.name,
                                        value: type.id,
                                    }))}
                                    onChange={handleChangeLicenseType}
                                    inputSize={'small'}
                                    value={licenseValue}
                                    disableClearable={true}
                                    variant={'outlined'}
                                    ariaLabel="license type"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    {addForm &&
                        addForm.sections.flatMap((section) =>
                            section.entries.map((entry) => (
                                <Grid key={entry.field} container style={{ marginTop: '10px', width: '100%' }}>
                                    <Grid
                                        key={entry.field}
                                        item
                                        style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                    >
                                        <FormFieldDisplay
                                            formData={formData}
                                            formField={entry}
                                            value={formData[entry.field]}
                                            error={formErrors[entry.field]}
                                            readOnly={false}
                                            handleInputChange={handleInputChange}
                                            isCustomStyle={true}
                                        />
                                    </Grid>
                                </Grid>
                            )),
                        )}
                </Box>
                <Grid className="btn-container" sm={12} md={12} lg={12} item>
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            onClick={() => {
                                setIsAddLicenseModalOpen(false);
                            }}
                            className="modal-btn"
                        >
                            CANCEL
                        </Button>
                        <Button
                            className="modal-btn"
                            onClick={handleAddLicenseChange}
                            variant="contained"
                            color="primary"
                            disabled={
                                licenseTypeId === '' ||
                                addFormLoading ||
                                Object.values(formErrors).some((error) => error.error)
                            }
                        >
                            ADD
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddLicenseDialog;
